import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, Sparkles, User, Clock, AlertTriangle, CheckCircle2,
  ArrowUpRight, Loader2, MessageSquare, XCircle,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const PRIORITY_TINT = {
  high:   { bg: "#c02a2a", label: "Prioritaire" },
  normal: { bg: "#009640", label: "Standard" },
  low:    { bg: "#6b7480", label: "Basse" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function fmtRole(role) {
  const map = { admin: "Administrateur", talent: "Talent", employeur: "Employeur",
    freelance: "Freelance", entrepreneur: "Entrepreneur", investisseur: "Investisseur",
    universite: "Université", coach: "Coach", "super-admin": "Super-admin" };
  return map[(role || "").toLowerCase()] || role || "Visiteur anonyme";
}


export default function AdminOrchestrator() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);          // escalation id being processed
  const [openModal, setOpenModal] = useState(null); // { esc, action: "authorize" | "redirect" }
  const [note, setNote] = useState("");
  const [priority, setPriority] = useState("normal");
  const [error, setError] = useState("");

  const token = () => "";  // deprecated — cookies handle auth now
  void token;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/africtal-orchestrator/pending");
      setPending(data.items || []);
    } catch (e) {
      console.warn("[admin-orch] load failed:", formatApiError(e));
      setError("Impossible de charger les demandes pour le moment.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!openModal) return;
    const { esc, action } = openModal;
    setBusy(esc.id);
    try {
      const url = action === "authorize"
        ? `/africtal-orchestrator/${esc.id}/authorize-ai`
        : `/africtal-orchestrator/${esc.id}/redirect-expert`;
      const body = action === "authorize"
        ? { priority, admin_note: note }
        : { reason: note, contact_channel: "email" };
      await api.post(url, body);
      setOpenModal(null);
      setNote("");
      setPriority("normal");
      await load();
    } catch (e) {
      console.warn("[admin-orch] submit failed:", formatApiError(e));
      setError("L'action n'a pas pu être finalisée. Merci de réessayer.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <PrivateLayout>
      <div className="afr-orchestrator" data-testid="admin-orchestrator-root">
        <header className="afr-orchestrator__head">
          <div>
            <span className="afr-eyebrow" style={{ color: "var(--afr-primary)" }}>Contrôle interne</span>
            <h1 style={{ fontFamily: "'Outfit',sans-serif", margin: "0.3rem 0 0.4rem", fontSize: "1.9rem" }}>
              Contrôle AFRICTAL Orchestrateur
            </h1>
            <p className="afr-muted" style={{ margin: 0, maxWidth: 720 }}>
              Chaque demande complexe transmise par l'Assistant d'Accueil ou lancée depuis un tableau de bord
              apparaît ici. Autorisez l'IA à produire un accompagnement personnalisé, ou réorientez vers un
              expert humain.
            </p>
          </div>
          <div className="afr-orchestrator__count" data-testid="admin-orchestrator-count">
            <ShieldCheck size={20} />
            <div>
              <strong>{pending.length}</strong>
              <span> en attente</span>
            </div>
          </div>
        </header>

        {error && (
          <div className="afr-callout afr-callout--warn" data-testid="admin-orchestrator-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {loading ? (
          <div className="afr-orchestrator__loading" data-testid="admin-orchestrator-loading">
            <Loader2 size={22} className="afr-spin" /> Chargement des demandes…
          </div>
        ) : pending.length === 0 ? (
          <div className="afr-orchestrator__empty" data-testid="admin-orchestrator-empty">
            <CheckCircle2 size={28} style={{ color: "var(--afr-primary)" }} />
            <h3>Aucune demande en attente</h3>
            <p className="afr-muted">
              Toutes les escalades ont été traitées. L'Assistant d'Accueil AFRICTAL prend le relais du premier
              niveau — vous serez notifié dès qu'une demande complexe arrivera.
            </p>
          </div>
        ) : (
          <ul className="afr-orchestrator__list" data-testid="admin-orchestrator-list">
            {pending.map((e) => {
              const prio = PRIORITY_TINT[e.priority] || PRIORITY_TINT.normal;
              return (
                <li key={e.id} className="afr-orchestrator__item" data-testid={`admin-orch-item-${e.id}`}>
                  <div className="afr-orchestrator__meta">
                    <span className="afr-orchestrator__prio" style={{ background: prio.bg }}>{prio.label}</span>
                    <span className="afr-flex-row" style={{ gap: 4 }}>
                      <User size={13} /> {e.user_snapshot?.name || e.user_email || "Visiteur anonyme"}
                    </span>
                    <span className="afr-muted" style={{ fontSize: "0.82rem" }}>· {fmtRole(e.user_snapshot?.role)}</span>
                    <span className="afr-flex-row afr-muted" style={{ gap: 4, fontSize: "0.82rem" }}>
                      <Clock size={12} /> {fmtDate(e.created_at)}
                    </span>
                  </div>
                  <p className="afr-orchestrator__msg">{e.message}</p>
                  <div className="afr-orchestrator__actions">
                    <button
                      type="button"
                      className="afr-btn afr-btn--primary afr-btn--sm"
                      onClick={() => { setOpenModal({ esc: e, action: "authorize" }); setNote(""); setPriority(e.priority || "normal"); }}
                      disabled={busy === e.id}
                      data-testid={`admin-orch-authorize-${e.id}`}
                    >
                      <Sparkles size={14} /> Autoriser IA
                    </button>
                    <button
                      type="button"
                      className="afr-btn afr-btn--outline afr-btn--sm"
                      onClick={() => { setOpenModal({ esc: e, action: "redirect" }); setNote(""); }}
                      disabled={busy === e.id}
                      data-testid={`admin-orch-redirect-${e.id}`}
                    >
                      <MessageSquare size={14} /> Réorienter vers un expert
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {openModal && (
        <div className="afr-modal" role="dialog" data-testid="admin-orch-modal" onClick={(ev) => ev.target === ev.currentTarget && setOpenModal(null)}>
          <div className="afr-modal__panel">
            <button type="button" className="afr-modal__close" onClick={() => setOpenModal(null)} aria-label="Fermer" data-testid="admin-orch-modal-close">
              <XCircle size={18} />
            </button>
            <h2 style={{ fontFamily: "'Outfit',sans-serif", marginTop: 0 }}>
              {openModal.action === "authorize" ? "Autoriser l'IA" : "Réorienter vers un expert"}
            </h2>
            <p className="afr-muted" style={{ fontSize: "0.94rem" }}>
              {openModal.action === "authorize"
                ? "L'Orchestrateur va analyser la demande, mobiliser uniquement les Coachs nécessaires et produire un plan d'action personnalisé pour l'utilisateur."
                : "L'utilisateur recevra un message l'informant qu'un expert AFRICTAL prendra contact avec lui prochainement."}
            </p>

            {openModal.action === "authorize" && (
              <label className="afr-field">
                <span>Priorité</span>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="admin-orch-modal-priority">
                  <option value="low">Basse</option>
                  <option value="normal">Standard</option>
                  <option value="high">Prioritaire</option>
                </select>
              </label>
            )}

            <label className="afr-field">
              <span>{openModal.action === "authorize" ? "Note interne (optionnel)" : "Motif de réorientation"}</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder={openModal.action === "authorize" ? "Contexte utile pour l'IA…" : "Pourquoi orienter vers un humain ?"}
                data-testid="admin-orch-modal-note"
              />
            </label>

            <div className="afr-flex-row" style={{ justifyContent: "flex-end", gap: "0.6rem", marginTop: "1rem" }}>
              <button type="button" className="afr-btn afr-btn--ghost afr-btn--sm" onClick={() => setOpenModal(null)}>Annuler</button>
              <button
                type="button"
                className={`afr-btn afr-btn--${openModal.action === "authorize" ? "primary" : "outline"} afr-btn--sm`}
                onClick={submit}
                disabled={!!busy}
                data-testid="admin-orch-modal-submit"
              >
                {busy ? <><Loader2 size={12} className="afr-spin" /> Traitement…</> : (openModal.action === "authorize" ? <>Autoriser <ArrowUpRight size={12} /></> : <>Confirmer <ArrowUpRight size={12} /></>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </PrivateLayout>
  );
}
